import { css } from "@emotion/react";
import { format, isValid, parse } from "date-fns";
import { ChangeEventHandler, RefObject, forwardRef, useEffect, useRef, useState } from "react";
import { FieldInputProps } from "react-final-form";

import { ChevronUp } from "@livechat/design-system-icons";
import { DatePicker, DesignToken, Icon, ShadowToken } from "@livechat/design-system-react-components";

import { InputFieldFullWidthFinalForm } from "components/design-system-adapters/InputField";
import Block from "components/layout/Block";
import { DateFormat } from "constants/date";
import { useClickOutside } from "hooks/useClickOutside";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { customFieldInputStyles } from "./TicketInfoCustomFieldStyles";

type ITicketDetailsCustomFieldDateProps = FieldInputProps<string, HTMLElement> & {
  handleFormReset?: () => void;
  disabled?: boolean;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: Record<string, any>;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta: Record<string, any>;
};

const customFieldIChevronStyles = css({ position: "absolute", top: "8px", right: "8px" });
const datePickerWrapperStyles = css({
  backgroundColor: withVar(DesignToken.SurfacePrimaryDefault),
  borderRadius: "4px",
  boxShadow: withVar(ShadowToken.PopOver),
  boxSizing: "border-box",
  margin: 0,
  padding: "4px 0",
  position: "absolute",
  zIndex: StackingContextLevel.Dropdown,
  top: "42px",
  ".rdp-week > *": {
    verticalAlign: "middle",
  },
});

// @ts-expect-error TS(2345): Argument of type '(props: any, ref: RefObject<any>... Remove this comment to see the full error message
export default forwardRef(TicketInfoCustomFieldDate);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
function TicketInfoCustomFieldDate(props: ITicketDetailsCustomFieldDateProps, ref: RefObject<any>) {
  const { input, meta, disabled, ...otherProps } = props;
  const initSelectedDate = isValid(parse(input.value)) ? parse(input.value) : undefined;
  // @ts-expect-error TS(23245
  const [selectedDate, setSelectedDate] = useState<Date>(initSelectedDate);
  const [isDatePickerOpen, setDatepickerOpen] = useState(false);
  const inputRef = ref;
  const datepickerRef = useRef(null);

  useClickOutside({ elementRef: datepickerRef, onClickOutside: () => setDatepickerOpen(false) });

  const handleKeyPress = (e: KeyboardEvent): void => {
    if (e.key === "Escape" || e.key === "Enter") {
      setDatepickerOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return (): void => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    const { value } = target;
    const date = parse(value);
    input.onChange(value);
    if (isValid(date)) {
      setSelectedDate(date);
    } else {
      // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
      setSelectedDate(undefined);
    }
  };

  const handleDaySelect = (date: Date): void => {
    setSelectedDate(date);
    if (date) {
      input.onChange(format(date, DateFormat.HtmlInputDate));
      setDatepickerOpen(false);
    } else {
      input.onChange("");
    }
  };

  return (
    <Block css={css({ position: "relative" })}>
      <InputFieldFullWidthFinalForm
        {...otherProps}
        disabled={disabled}
        input={input}
        meta={meta}
        ref={inputRef}
        css={[css({ width: "100%" }), customFieldInputStyles]}
        placeholder={!isDatePickerOpen ? "No value provided" : DateFormat.HtmlInputDate}
        onChange={handleInputChange}
        onFocus={() => setDatepickerOpen(true)}
      />
      {isDatePickerOpen && (
        <div ref={datepickerRef}>
          <div onClick={() => setDatepickerOpen(false)} role="button">
            <Icon css={customFieldIChevronStyles} source={ChevronUp} />
          </div>
          <div css={datePickerWrapperStyles}>
            <DatePicker selected={selectedDate} onDayClick={handleDaySelect} mode="single" />
          </div>
        </div>
      )}
    </Block>
  );
}
